/**
 * Meta (Facebook / Instagram) Pixel events for web ad campaigns.
 *
 * The base pixel (fbq stub, fbevents.js, init + first PageView) loads from
 * index.html's deferred analytics loader, next to GA4. fbevents.js counts SPA
 * route changes as PageViews by itself. This module maps the dataLayer.ts
 * events onto it: dataLayer's sendToGA4 hands every event here, and only the
 * ad-funnel ones go out — under the same names the app SDK uses
 * (shared/metaAdEvents.ts).
 */
import { isMetaFunnelCustomEvent } from "@shared/metaAdEvents";

type Params = Record<string, unknown>;

export interface MetaPixelEvent {
  command: "track" | "trackCustom";
  name: string;
  data: Record<string, string | number | boolean | string[]>;
  /** Meta's dedup key — lets a later server-side (CAPI) copy merge in. */
  eventId?: string;
}

const STANDARD_EVENTS: Record<string, string> = {
  sign_up: "CompleteRegistration",
  begin_checkout: "InitiateCheckout",
  purchase: "Purchase",
};

// Event params worth having in Events Manager (breakdowns, custom
// conversions). Everything else — project ids, prompts, error text, the
// GA4 items array — stays out of Meta.
const PASSTHROUGH_PARAMS = [
  "value",
  "currency",
  "method",
  "source",
  "trigger",
  "project_mode",
  "project_mode_label",
  "video_type",
  "resolution",
  "aspect_ratio",
  "funnel_step",
  "plan_id",
  "billing_period",
  "purchase_kind",
] as const;

function pickParams(params: Params): MetaPixelEvent["data"] {
  const data: MetaPixelEvent["data"] = {};
  for (const key of PASSTHROUGH_PARAMS) {
    const value = params[key];
    if (typeof value === "string" && value.length > 0) data[key] = value;
    else if (typeof value === "number" && Number.isFinite(value))
      data[key] = value;
    else if (typeof value === "boolean") data[key] = value;
  }
  return data;
}

function readItems(params: Params): Array<Record<string, unknown>> {
  return Array.isArray(params.items)
    ? (params.items.filter((item) => item && typeof item === "object") as Array<
        Record<string, unknown>
      >)
    : [];
}

/**
 * The Pixel call for a GA4 event name + params, or null when the event isn't
 * part of the ad funnel. Funnel steps that ended in an error are dropped: on
 * Meta a "*_complete" event is a success signal campaigns can optimize for.
 */
export function metaPixelEventFor(
  eventName: string,
  params: Params = {},
): MetaPixelEvent | null {
  const standardName = STANDARD_EVENTS[eventName];
  if (standardName) {
    const data = pickParams(params);
    const items = readItems(params);
    if (items.length) {
      const ids = items
        .map((item) => item.item_id)
        .filter((id): id is string => typeof id === "string" && !!id);
      if (ids.length) {
        data.content_ids = ids;
        data.content_type = "product";
      }
      data.num_items = items.reduce(
        (sum, item) =>
          sum + (typeof item.quantity === "number" ? item.quantity : 1),
        0,
      );
      const category = items[0]?.item_category;
      if (typeof category === "string" && category)
        data.content_category = category;
    }
    const transactionId = params.transaction_id;
    return {
      command: "track",
      name: standardName,
      data,
      ...(eventName === "purchase" &&
        typeof transactionId === "string" &&
        transactionId && { eventId: transactionId }),
    };
  }

  if (!isMetaFunnelCustomEvent(eventName)) return null;
  if (params.status === "error") return null;
  const transactionId = params.transaction_id;
  return {
    command: "trackCustom",
    name: eventName,
    data: pickParams(params),
    ...(typeof transactionId === "string" &&
      transactionId && { eventId: `${eventName}:${transactionId}` }),
  };
}

/**
 * Meta advanced matching: the signed-in user's email and account id, which
 * fbevents.js SHA-256-hashes in the browser before anything is sent. Lets Meta
 * tie a purchase to an ad click when the click cookie is gone (the buyer
 * clicked on their phone and paid on a laptop). The server-side events send
 * Meta the same hashed email + id (server/metaCapiClient.ts), the account id
 * lowercased before hashing, so it is lowercased here too. On since Nov 2026
 * (Kitty's call); VITE_META_PIXEL_ADVANCED_MATCHING=off turns it off.
 */
export function setMetaPixelUser(
  user: { id: string; email?: string | null },
  flag: string | undefined = import.meta.env.VITE_META_PIXEL_ADVANCED_MATCHING,
): void {
  if (flag === "off" || typeof window === "undefined") return;
  const userData: Record<string, string> = {
    external_id: user.id.trim().toLowerCase(),
  };
  const email = user.email?.trim().toLowerCase();
  if (email) userData.em = email;
  // index.html's loader reads this when the pixel loads later (it waits for
  // the first interaction or 3 s); if the pixel is already up, re-init.
  window.__solmiMetaUserData = userData;
  const pixelId = window.__solmiMetaPixelId;
  if (!pixelId || typeof window.fbq !== "function") return;
  try {
    window.fbq("init", pixelId, userData);
  } catch (err) {
    console.warn("[MetaPixel] advanced matching failed", err);
  }
}

/** Send a dataLayer event to the Meta Pixel if it's an ad-funnel event. */
export function sendToMetaPixel(eventName: string, params?: object): void {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  const event = metaPixelEventFor(eventName, (params ?? {}) as Params);
  if (!event) return;
  try {
    if (event.eventId) {
      window.fbq(event.command, event.name, event.data, {
        eventID: event.eventId,
      });
    } else {
      window.fbq(event.command, event.name, event.data);
    }
  } catch (err) {
    console.warn("[MetaPixel] send failed", eventName, err);
  }
}
